import React from 'react';

import { validServiceType } from '~support-at-home/lib/derive';

import { TooltipPortal } from '~components';
import { faInfoCircle } from '~components/icon';
import { Row } from '~components/table-view';
import HoverPopover from '~support-at-home/components/common/HoverPopover';

import { ServiceTypeRow } from '~appointments/reducers/serviceTypes';

// The pointer crosses this column on its way to the rest of the row, so the popover only opens
// once it has actually come to rest - otherwise a sweep across the table fires one per row.
const INFO_HOVER_DELAY_MS = 250;

interface ComponentProps {
  row: Row;
  serviceTypes: ServiceTypeRow[];
  /** Lines describing the Support at Home service this entry resolves to, empty if unresolved. */
  serviceDetail: string[];
}

/**
 * A price plan entry's local service type, with the Support at Home service it resolves to behind
 * an info icon.
 *
 * The service period used to have a column of its own. It was confusing there: the plan header
 * already carries a start and end date, so a second set of dates on every row read as though the
 * row itself were dated. It is really a property of the government service the entry is tied to,
 * which is exactly the kind of thing that belongs beside the service rather than in the grid - and
 * this table has six rate columns competing for width, so a 200px column spent on it was the most
 * expensive place to put it.
 */
// Called directly as `ServiceTypeCell({ row, serviceTypes, serviceDetail })` from
// pricePlanServicesList.ts's `renderReadOnly` (a .ts file, so no JSX is available at the call
// site), not rendered as `<ServiceTypeCell />` - typed as a plain function rather than `React.FC`
// so it reads as the render helper it is, not a component that could safely pick up a hook.
const ServiceTypeCell = ({ row, serviceTypes, serviceDetail }: ComponentProps) => {
  const serviceType = validServiceType(row, serviceTypes);

  if (!serviceType) {
    return (
      <>
        <TooltipPortal targetId={`${row.id}-missing-service`} content="Service deleted or not found." />
        <div className="missing-cell" id={`${row.id}-missing-service`}>
          DELETED
        </div>
      </>
    );
  }

  return (
    <div className="price-plan-service-cell">
      <span className="price-plan-service-cell-name">{serviceType.name}</span>
      {serviceDetail.length > 0 && (
        <HoverPopover
          id={String(row.id)}
          icon={faInfoCircle}
          variant="info"
          triggerClassName="text-info"
          header="Support at Home service"
          messages={serviceDetail}
          ariaLabel={`Support at Home service detail for ${serviceType.name}`}
          dataCyPrefix="price-plan-service-info"
          placement="right"
          openDelay={INFO_HOVER_DELAY_MS}
        />
      )}
    </div>
  );
};

export default ServiceTypeCell;
